import PaymentHelper from "@/utils/payment-helper"
import apiClient, { removeValuesWithObjectType } from "@/utils/cb-client"
import Transformer from "@/utils/transformer"
import BulkApi from "@/utils/bulk-api";
import gstHelper from "@/utils/gst-helper"
import cloneDeep from 'lodash-es/cloneDeep';

class BulkApiHelper {
  
  setConfig(state, getters) {
    this.state = state;
    this.getters = getters;
    PaymentHelper.setConfig(state, getters)
  }

  setRequestApiName(bulkAction, apiName) {
    return Object.assign(bulkAction, { apiName })
  }

  sanitizeObj(obj) {
    const sanitizedObj = removeValuesWithObjectType(obj);
    // Assign hp Token to object
    sanitizedObj.hp_token = this.state.auth.hpToken;
    return sanitizedObj;
  }

  updateCustomerDetails() {
    const customerData = this.getters.customer_data
    const sections = this.getters.checkout_sections;
    const getFieldNames = (fields) => fields.map(field => field.name);

    let customerDataFields = getFieldNames(sections.customer.fields)
    customerDataFields = customerDataFields.concat(getFieldNames(sections.consent.fields))
    
    let payload = {
      id: customerData.id
    }
    customerDataFields.map(fieldName => {
      payload[fieldName] = customerData[fieldName]
    })

    payload = this.sanitizeObj(payload);
    return apiClient.customers.update_bulk_action(payload)
  }

  updateBillingInfo() {
    // GST - Only for checkout
    const addressSection = this.getters.checkout_sections.billing_address;
    if (addressSection && addressSection.gst_config) {
      gstHelper.setGstConfig(JSON.parse(addressSection.gst_config));
    }

    const addressData = this.state.customer.billing_address;
    const customerData = this.state.customer.data;
    if(gstHelper.isGstRequested() && addressData.country === 'AU') {
      addressData.registered_for_gst = customerData.registered_for_gst
    }
    let params = Transformer.flatten(addressData, "billing_address");
    if (this.state.customer.data.vat_number) {
      params.vat_number = this.state.customer.data.vat_number;
      if (this.state.customer.data.vat_number_prefix) {
        params.vat_number_prefix = this.state.customer.data.vat_number_prefix;
      }
      if (this.state.config.einvoice_base_configuration.collectPublicIdentifiersCheckout && this.state.customer.data.entity_identifier_scheme) {
        params.entity_identifier_scheme = this.state.customer.data.entity_identifier_scheme;
      }
    }

    Object.assign(params, this.updateEinvoiceData(this.state));
    params.id = this.getters.customer_id;
    params = this.sanitizeObj(params)
    return apiClient.customers.update_billing_info_bulk_action(params)
  }

  updatePaymentMethod() {
    const pmType = this.getters.selected_payment_method.type
    const params = {
      pmType,
      replacePrimary: false,
      pm: this.getters.pm_by_type(pmType),
      data: this.getters.selected_payment_method,
      intent: this.getters.payment_intent
    }
    
    return PaymentHelper.doBulkAction(params)
  }

  getBulkAction(key) {
    switch (key) {
      case 'data': // Customer data
        return this.updateCustomerDetails();
      case 'billing_address':
        return this.updateBillingInfo();
      case 'payment': {
        if(
          PaymentHelper.isOfflinePaymentMethod(this.getters.selected_payment_method.type) ||
          PaymentHelper.isPaymentIntentTransaction()
        ) {
          return null;
        }
        const bulkAction = this.updatePaymentMethod()
        return this.setRequestApiName(bulkAction, 'create_payment_method');
      }
    }
  }

  checkCustomerDetailsModified(bulkApiRequest, skipPm) {
    // Add bulk actions for update customer, update billing info, etc. if required
    const modifications = this.state.customer.modified
    if(skipPm) {
      delete modifications.payment;
    }
    Object.keys(modifications).map(key => {
      // CHKOUTENGG-9368
      // Add customer update for all checkout new for existing customer and checkout existing
      // bulk API operations. To allow API level overrides for both customer & custom field params
      if ((modifications[key] && key !== 'data') || 
        (key == 'data' && !this.getters.is_checkout_gift) || 
        (key == "billing_address" && 
          (this.state.customer.entity_identifiers_hp && this.state.customer.entity_identifiers_hp.length) && !this.getters.is_checkout_gift)) {
        const bulkAction = this.getBulkAction(key);
        if(bulkAction) bulkApiRequest.addRequest(bulkAction)
      }
    })
  }

  getPaymentMethodId(bulkApiRequest) {
    const hasNewPaymentMethod = this.state.customer.modified.payment;
    if (hasNewPaymentMethod) {
      // Check request id for create payment method bulk action
      const contentId = bulkApiRequest.getRequestIdByApiName('create_payment_method')

      if (contentId && contentId+'') {
        return BulkApi.getResponseKey(contentId, 'payment_source.id')
      }
    }
    if(this.state.order.payment_method)
      return this.state.order.payment_method.id
  }

  /* CHKOUTENGG-28805 - 
  - should not attach if customer does not have payment method at all.
  - should not attach order payment_source_id if it is same as customer's primary payment source id.
   this will ensure that order payment method will be added as customer's default against subscription and 
   not attached to subscription level.
  */
  checkIfCustomersPrimaryPaymentMethod(paymentSourceId) {
    if (this.getters.customer_data
      && !this.getters.customer_data.payment_method) {
      return true;
    }
    if (paymentSourceId
      && this.getters.primary_pm
      && this.getters.primary_pm.id === paymentSourceId) {
      return true;
    }
    return false;
  }
  // CHKOUTENGG-28805 - should not call override billing profile if existing attached subscrption payment id is same as order payment source id.
  checkIfSubsAttachedPaymentMethod(paymentSourceId) {
    if (paymentSourceId && this.getters.subscription_data &&
      this.getters.subscription_data.subscription_pm_id === paymentSourceId) { // subscription_pm_id will not be nullable if payment method attached at subscription level
      return true;
    }
    return false;
  }

  shouldIncludePmId() {
    if(PaymentHelper.isPaymentIntentTransaction()) return false;
    // Check if multiple payment methods are configured for customer 
    const has_multiple_pms_configured = this.getters.payment_method_list.length > 1;

    // Construct dependent payment method ID to request only if 
    // adding multiple PMs is allowed & more than one PM is configured for customer
    // Note: If only one new pm is present for customer, no need to specify pm id, it is automatically taken
    if (this.getters.mpm_supported || has_multiple_pms_configured) {
      return true
    }
    return false
  }

  /* API used for =>
    : CHECKOUT_NEW 
    : CHECKOUT_EXISTING 
    : CHECKOUT_ONE_TIME
    ** for CHECKOUT_NEW + new customer -> createCustomer API is used
    # used when Hosted Page API is passed with params
  */
  updateEinvoiceData(state) {
    if (state.checkout.data.type != "checkout_gift") {
      /* Einvoice entity-identifiers array */
      let entityIdentifiers;
      if (state.customer.entity_identifiers_combined.length) {
        /* data edited & processed in Billing Section */
        entityIdentifiers = Transformer.transformEinvoiceForBillingApi(state.customer.entity_identifiers_combined);
      } else {
        /* data directly from HP API params + billing_country code is MANDATORY */
        /* already saved entity_identifiers for customer are not updated, hence not required here */
        if (state.customer.entity_identifiers_hp && state.customer.entity_identifiers_hp.length && state.customer.billing_address.country) {
          let result = cloneDeep(state.customer.entity_identifiers_hp);
          let prepareData = [];
          for (let item of result) { 
            if ((!item.operation || item.operation == "") && (!item.id || item.id == "")) {
              item.operation = "CREATE";
            }
            prepareData.push({
              id: item.id,
              scheme: item.scheme,
              value: item.value,
              operation: item.operation
            });
          }

          entityIdentifiers = Transformer.transformEinvoiceForBillingApi(prepareData);
        } else {
        /* if no params from HP API, then no change */
          entityIdentifiers = {};
        }
      }

      return entityIdentifiers;
    }

    return {};
  }
}

export default new BulkApiHelper()